/**
 * ============================================================================
 * Experience — derived from the content layer (single source of truth).
 * ============================================================================
 *
 * Real data lives in `src/content/data/experience.json` and flows through the
 * typed loader (`src/content`). This file only adapts the canonical
 * `ExperienceEntry` shape into the `ExperienceRole` shape the Experience_v2
 * timeline renders. To change what's displayed, edit the JSON — not this file
 * and not the component.
 *
 * The previous placeholder entries ("Your current job title", `placeholder:
 * true` sample cards) have been replaced by the four canonical roles from the
 * master resume. The `placeholder` flag is kept on the interface so the
 * component's "Sample entry" guard still compiles, but no real entry sets it.
 */

import { experience } from "../content";
import type { Achievement, ExperienceEntry } from "../types/content";
import { companyMark } from "../utils/companyLogos";

export interface ExperienceRole {
	id: string;
	role: string;
	company: string;
	companyUrl?: string;
	/** "YYYY-MM" */
	start: string;
	/** "YYYY-MM", or null while still in the role. */
	end: string | null;
	employmentType?: string;
	location?: string;
	/** Company mark for the spine node. Falls back to the company's initials. */
	logo?: string;
	/** One line on the remit — what you owned, not what you were told to do. */
	summary?: string;
	/** Outcomes beat responsibilities. Lead with what changed because you were there. */
	highlights: string[];
	stack: string[];
	/** Remove once real content is in place. */
	placeholder?: boolean;
}

/**
 * Timeline cards stay scannable: featured achievements (capped at five). If an
 * entry has fewer than two featured items, fall back to its first three
 * achievements. The complete, uncapped list remains in experience.json.
 */
const toHighlights = (entry: ExperienceEntry): string[] => {
	const featured = entry.achievements.filter((a: Achievement) => a.featured).map((a: Achievement) => a.text);
	if (featured.length >= 2) return featured.slice(0, 5);
	return entry.achievements.slice(0, 3).map((a: Achievement) => a.text);
};

export const experienceRoles: ExperienceRole[] = experience.map((entry: ExperienceEntry) => ({
	id: entry.id,
	role: entry.title,
	company: entry.company,
	start: entry.startDate,
	end: entry.endDate,
	employmentType: "Full-time",
	location: entry.location,
	summary: entry.summary,
	/* The company's mark for the spine node, or undefined — `Experience_v2`
	   falls back to initials, which is the state this spine shipped in from
	   D-038 until D-098 supplied the four marks. */
	logo: companyMark(entry.id),
	highlights: toHighlights(entry),
	stack: entry.tags
}));

/**
 * Formats "YYYY-MM" as "Mar 2020". Parsed manually rather than through Date,
 * because `new Date("2020-03")` is treated as UTC and can render as February
 * in timezones behind GMT.
 */
export const formatMonth = (value: string): string => {
	const [year, month] = value.split("-").map(Number);
	const names = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
	return `${names[month - 1]} ${year}`;
};

/**
 * Inclusive tenure between two "YYYY-MM" points, as "2 yrs 3 mos".
 * A role that starts and ends in the same month counts as one month, not zero.
 */
export const formatTenure = (start: string, end: string | null, now = new Date()): string => {
	const [sy, sm] = start.split("-").map(Number);
	const [ey, em] = end ? end.split("-").map(Number) : [now.getFullYear(), now.getMonth() + 1];

	const total = (ey - sy) * 12 + (em - sm) + 1;
	if (total <= 0) return "";

	const years = Math.floor(total / 12);
	const months = total % 12;
	const parts: string[] = [];

	if (years) parts.push(`${years} yr${years > 1 ? "s" : ""}`);
	if (months) parts.push(`${months} mo${months > 1 ? "s" : ""}`);

	return parts.join(" ");
};
